import { useMemo } from 'react';

import { useDeepMemo } from '@/hook/useDeepMemo';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { EXAMPLE_CONTENT_REEL_SLUG, Reel } from '@/types/reel.type';
import { DataState, SUCCESS_DATA_STATE } from '@/types/data.type';

import { useAppDispatch, useAppSelector } from '../store';

import {
    upsertReels,
    resetReels,
    setReelName,
    setReelsDataState,
    REELS_REDUCER_NAME,
} from './reels.slice';

// SELECTORS
export function useReels() {
    return useAppSelector((state) => state[REELS_REDUCER_NAME]);
}

export function useReelsDataMap() {
    return useAppSelector((state) => state.reels.data);
}

// NOTE: Deep-memoized, as refreshed reels usually come back unchanged
export function useReelsData() {
    const reelsDataMap = useReelsDataMap();

    return useDeepMemo(() => Object.values(reelsDataMap), reelsDataMap);
}

export function useReelsDataByCode(code?: string | null): Reel | undefined {
    return useAppSelector((state) =>
        code ? state.reels.data[code] : undefined,
    );
}

export function useExampleContentReel(): Reel | undefined {
    const reels = useReelsData();

    return useMemo(
        () =>
            reels.find(
                (reel) => reel.example_reel_slug === EXAMPLE_CONTENT_REEL_SLUG,
            ),
        [reels],
    );
}

export function useReelsDataStateMap() {
    return useAppSelector((state) => state.reels.dataStateMap);
}

// NOTE: A reel counts as loaded while it's being refreshed
export function useAreReelsLoaded(codes: (string | null | undefined)[]) {
    return useAppSelector((state) =>
        codes.every(
            (code) =>
                !code ||
                !!state.reels.data[code] ||
                state.reels.dataStateMap[code] === SUCCESS_DATA_STATE,
        ),
    );
}

// DISPATCHERS
export function useUpsertReels() {
    const dispatch = useAppDispatch();

    return useInteractiveCallback((reels: Reel[]) => {
        dispatch(upsertReels({ reels }));
    });
}

export function useResetReels() {
    const dispatch = useAppDispatch();

    return useInteractiveCallback(() => {
        dispatch(resetReels());
    });
}

export function useSetReelName() {
    const dispatch = useAppDispatch();

    return useInteractiveCallback((reelCode: string, name: string) => {
        dispatch(
            setReelName({
                reelCode,
                name,
            }),
        );
    });
}

export function useSetReelsDataState() {
    const dispatch = useAppDispatch();

    return useInteractiveCallback((dataStateMap: Record<string, DataState>) => {
        dispatch(setReelsDataState(dataStateMap));
    });
}
